import { createFileRoute, Link } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { useT } from "@/glossary";
import {
  EXERCISE_IDS,
  EXERCISES,
  type ExerciseId,
  useEverythingFinishedOnce,
  useExerciseStates,
} from "@/lib/exercises";
import { formatScore } from "@/lib/score";
import { useUserPrefs } from "@/lib/stores/user-prefs";

export const Route = createFileRoute("/")({
  component: RouteComponent,
});

type ExerciseState = ReturnType<typeof useExerciseStates>[ExerciseId];

const ExerciseCard = ({
  id,
  state,
}: {
  id: ExerciseId;
  state: ExerciseState;
}) => {
  const t = useT();
  const exercise = EXERCISES[id];
  const progress = state?.progress ?? 0;
  const total = state?.total ?? exercise.total;

  return (
    <div className="flex flex-col gap-2">
      <Button as={Link} to={exercise.path} className="text-center">
        {exercise.title(t)}
      </Button>
      <p className="text-right text-brand">
        {state?.isFinished
          ? t.home.score(formatScore(state.score), total)
          : progress === 0 && state?.wasEverFinished
            ? t.home.score(formatScore(state.lastScore), total)
            : t.home.progress(progress, total)}
      </p>
    </div>
  );
};

function RouteComponent() {
  const t = useT();
  const userPrefs = useUserPrefs();
  const states = useExerciseStates();
  const everythingFinishedOnce = useEverythingFinishedOnce();

  return (
    <div className="flex flex-col gap-6 p-4">
      {userPrefs?.username && (
        <p className="text-center text-2xl text-muted-foreground">
          {t.home.welcome(userPrefs.username)}
        </p>
      )}
      <div className="flex w-full flex-col gap-4">
        {EXERCISE_IDS.map((id) => (
          <ExerciseCard key={id} id={id} state={states[id]} />
        ))}
      </div>

      {everythingFinishedOnce && (
        <Link
          to="/summary"
          className="text-center text-brand underline-offset-4 hover:underline"
        >
          {t.home.summaryLink}
        </Link>
      )}

      <IconButton
        as={Link}
        to="/settings"
        className="absolute bottom-4 left-4 border border-foreground"
        aria-label={t.settings.title}
      >
        <Settings size={20} />
      </IconButton>
    </div>
  );
}
